<template>
  <default-layout show-leaderboard-ad>
    <div class="forums-index" data-test-id="forums-index">
      <forums-index-header
        :term="term"
        @handleSearch="handleSearch"
      />
      <div class="forums-index__browsing-controls">
        <forum-sort
          class="forums-index__sort"
          data-test-id="forums-index-sort"
          :selected="sort"
          @change="updateSort"
        />
        <forum-tag-select
          v-if="forumTags.length > 0"
          class="forums-index__tag-select"
          :forum-tags="forumTags"
        />
      </div>
      <div
        v-if="term.length > 0"
        class="forums-index__action"
      >
        <router-link
          :to="{ name: 'forums' }"
          class="forums-index__back"
        >
          <icon-component
            name="caret"
            title="caret icon"
            class="forums-index__icon icon--small"
          />
          <span class="forums-index__back-text">
            Back to all discussions
          </span>
        </router-link>
      </div>
      <div
        v-if="term"
        class="forums-index__showing-results-text"
      >
        Showing results for '{{ term }}'
      </div>
      <div class="forums-index__content">
        <loading-spinner v-if="loading !== 0" />
        <div v-else class="forums-index__threads">
          <paginated-list
            :items="results"
            :current-page="page"
            :total-count="totalCount"
            :per-page="perPage"
            class="forums-index__thread"
            data-test-id="forums-index-thread"
            exclude-ads
            :empty-message="emptyMessage"
          />
          <create-thread-cta v-if="showForumCta" />
        </div>
        <div
          class="forums-index__tag-picker"
          data-test-id="forums-index-tag-picker"
        >
          <tag-picker
            :forum-tags="tagOptions"
            :within-form="false"
          />
        </div>
      </div>
    </div>
  </default-layout>
</template>

<script>
import { ALL_FORUM_TAGS_QUERY } from '@/graphql/queries/forum-queries';
import { SEARCH_QUERY } from '@/graphql/queries/search-queries';
import ForumsIndexHeader from '@/components/Forum/ForumsIndexHeader.vue';
import TagPicker from '@/components/Forum/TagPicker.vue';
import ForumSort from '@/components/Forum/ForumSort.vue';
import ForumTagSelect from '@/components/Forum/ForumTagSelect.vue';
import PaginatedList from '@/components/Pagination/PaginatedList.vue';
import CreateThreadCta from '@/components/Forum/CreateThreadCta.vue';
import { mapGetters } from 'vuex';
import { collectionJsonLd } from '@/utils/structured-data';
import routeMixin from '@/mixins/route-mixin';

export default {
  name: 'ForumsIndex',
  components: {
    ForumsIndexHeader,
    TagPicker,
    PaginatedList,
    ForumSort,
    ForumTagSelect,
    CreateThreadCta,
  },
  mixins: [routeMixin],
  data() {
    return {
      results: [],
      forumTags: [],
      searchTags: [],
      dataLayer: {},
      loading: 0,
      perPage: 10,
      totalCount: 0,
      sort: this.$route.query.sort || 'RECENT',
      emptyMessage: 'Sorry, we couldn’t find any topics that match.',
    };
  },
  computed: {
    ...mapGetters([
      'refetchForumIndex',
      'selectedTags',
    ]),
    page() {
      return (this.$route.query.page && parseInt(this.$route.query.page, 10)) || 1;
    },
    showForumCta() {
      return this.page <= 1 && this.results.length < this.perPage;
    },
    tagOptions() {
      if (this.term) {
        return this.forumTags.filter(
          (tag) => this.searchTags.includes(tag.id),
        );
      }
      return this.forumTags;
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage);
    },
    metaDescription() {
      const pageInfo = `page ${this.page} of ${this.totalPages}`;
      return `${this.$site.defaultMeta['metadesc-forum']} ${pageInfo}`;
    },
    term() {
      return this.$route.query.term || '';
    },
  },
  metaInfo() {
    if (!this.$store.getters.siteHasFeature('Forums') || this.pageIsOnTwoOrMore) {
      return {
        title: 'Forums',
        meta: [
          {
            name: 'robots',
            vmid: 'robots',
            content: 'noindex',
          },
        ],
        link: [
          { vmid: 'canonical', content: null },
        ],
      };
    }
    return {
      title: 'Forums',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.metaDescription,
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.metaDescription,
        },
        {
          vmid: 'twitter:description',
          property: 'twitter:description',
          content: this.metaDescription,
        },
      ],
    };
  },
  watch: {
    selectedTags() {
      if (this.$route.name === 'forums') {
        this.handleTagSelection();
      }
    },
    results(to) {
      if (!to) { return; }
      this.onLoad();
    },
  },
  beforeCreate() {
    const tagIdsFromRoute = this.$route.query.tagId;
    if (!process.browser && tagIdsFromRoute && tagIdsFromRoute.length) {
      this.$store.dispatch('setSelectedTags', tagIdsFromRoute);
    }
  },
  mounted() {
    if (this.refetchForumIndex) {
      this.$apollo.queries.search.refetch()
        .then(() => {
          this.$store.dispatch('setRefetchForumIndex', false);
        });
    }
  },
  methods: {
    handleSearch(term) {
      if (this.term === term) {
        return;
      }
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          term,
          page: 1,
        },
      });
    },
    handleTagSelection() {
      this.$router.replace({
        name: 'forums',
        query: {
          ...this.$route.query,
          ...{ tagId: this.selectedTags },
          page: 1,
        },
      });
    },
    updateSort(value) {
      this.sort = value;
      this.$router.push({
        name: 'forums',
        query: {
          ...this.$route.query,
          ...{ sort: this.sort },
          page: 1,
        },
      });
    },
    setJsonLd() {
      this.$store.dispatch(
        'setContentJsonLd',
        collectionJsonLd(
          'Forum',
          this.totalCount,
          this.$route.path,
          this.$root.hostname,
          this.$site.settings.condition_name,
        ),
      );
    },
    onLoad() {
      this.setJsonLd();
      this.$store.dispatch('updateGlobalContext', {
        search: {
          type: 'Forums Index',
          query: this.results.map((r) => r.id),
        },
        featuredContent: {
          location: 'Forums Index',
          ids: this.results.filter((r) => r.isFeatured).map((r) => r.id),
        },
      });
      const dataLayer = JSON.stringify({
        ...this.dataLayer,
        ...{
          post_categories: ['forums'],
          has_user_generated_content: true,
        },
      });
      this.$emit('loaded', dataLayer);
    },
  },
  apollo: {
    search: {
      query: SEARCH_QUERY,
      variables() {
        return {
          page: this.page,
          query: this.term,
          index: 'FORUM',
          searchSort: this.sort,
          tagIds: this.selectedTags,
          limit: this.perPage,
          includeEngagement: true,
        };
      },
      result({ data }) {
        if (data) {
          if (!process.browser) {
          // make sure that we set the jsonLD
          // for the page during SSR
            this.setJsonLd();
          }
          if (!data.search || !data.search.searchResults) { return; }

          this.dataLayer = JSON.parse(data.search.dataLayer);
          this.totalCount = data.search.total;
          this.searchTags = data.search.tagIds;
          this.results = data.search.searchResults;
        } else {
          this.results = [];
        }
      },
    },
    forumTags: {
      query: ALL_FORUM_TAGS_QUERY,
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_forums-index';
</style>

<style lang="scss"> // no scoped - to reach html content
  @import '@/stylesheets/legacy/_highlight';
</style>
